import { useLayoutEffect, useRef, useState, type ReactElement } from 'react';
import { useUiOverlay } from '../../contexts/useUiOverlay';
import type { MorphRect } from '../../contexts/UiOverlayContext';

/** Target dimensions for the morphed chat panel (screen space). */
const MORPH_TARGET = {
  left: 24,
  top: 24,
  width: 400,
  height: 560,
};

/**
 * Visual FLIP morph from the home prompt card into the chat panel slot.
 * Does NOT contain chat or brief content — canvas PanelShapes own that.
 */
export function HomeChatMorphOverlay(): ReactElement | null {
  const { morphOrigin, homeVisible, clearMorphOrigin } = useUiOverlay();
  const [visible, setVisible] = useState(false);
  const morphedOrigin = useRef<MorphRect | null>(null);
  const shellRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (homeVisible || !morphOrigin || morphedOrigin.current === morphOrigin) {
      return undefined;
    }
    morphedOrigin.current = morphOrigin;
    setVisible(true);

    const el = shellRef.current;
    if (!el) return undefined;

    const prefersReduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced) {
      window.setTimeout(() => {
        setVisible(false);
        clearMorphOrigin();
      }, 0);
      return undefined;
    }

    const first = morphOrigin;
    const last = MORPH_TARGET;
    const dx = first.left - last.left;
    const dy = first.top - last.top;
    const sx = Math.max(0.05, first.width / last.width);
    const sy = Math.max(0.05, first.height / last.height);

    el.style.transformOrigin = 'top left';
    el.style.transition = 'none';
    el.style.transform = `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`;
    el.style.opacity = '1';
    void el.getBoundingClientRect();

    let settled = false;
    const settle = (): void => {
      if (settled) return;
      settled = true;
      setVisible(false);
      clearMorphOrigin();
    };

    const onEnd = (event: TransitionEvent): void => {
      if (event.propertyName === 'transform' || event.propertyName === 'opacity') {
        settle();
      }
    };
    el.addEventListener('transitionend', onEnd);
    const fallback = window.setTimeout(settle, 900);

    const raf = requestAnimationFrame(() => {
      if (!shellRef.current) {
        settle();
        return;
      }
      shellRef.current.style.transition =
        'transform 0.46s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.32s ease 0.38s';
      shellRef.current.style.transform = 'none';
      shellRef.current.style.opacity = '0';
    });

    return () => {
      el.removeEventListener('transitionend', onEnd);
      window.clearTimeout(fallback);
      cancelAnimationFrame(raf);
    };
  }, [morphOrigin, homeVisible, clearMorphOrigin]);

  if (!visible || homeVisible) {
    return null;
  }

  return (
    <div
      className="home-chat-morph"
      data-testid="home-chat-morph-overlay"
      aria-hidden
      ref={shellRef}
      style={{
        position: 'fixed',
        left: MORPH_TARGET.left,
        top: MORPH_TARGET.top,
        width: MORPH_TARGET.width,
        height: MORPH_TARGET.height,
        zIndex: 35,
        pointerEvents: 'none',
      }}
    >
      <div className="home-chat-morph__card">
        <div className="home-chat-morph__bar" />
        <div className="home-chat-morph__body" />
        <div className="home-chat-morph__composer">
          <span className="home-chat-morph__pill">Landing Pages</span>
          <span className="home-chat-morph__submit" />
        </div>
      </div>
    </div>
  );
}
